import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase"; // ensure you export both in ../firebase
import {
  doc,
  getDoc,
  getDocs,
  collectionGroup,
  query,
  where,
} from "firebase/firestore";

/**
 * Success page that fetches LIVE order details.
 * Priority: REST API (VITE_ORDER_API_URL) -> Firestore orders/{orderId} ->
 * users/{uid}/orders/{orderId} -> collectionGroup('orders') by paymentId
 */
function Success() {
  const navigate = useNavigate();
  const location = useLocation();
  const confettiRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  // Pull possible IDs from router state & query
  const { orderId, paymentId } = useMemo(() => {
    const st = location.state || {};
    const qs = new URLSearchParams(location.search || "");
    const first = (...keys) => keys.find((k) => st[k]) || keys.find((k) => qs.get(k));
    const oidK = first("orderId", "order_id", "razorpay_order_id");
    const pidK = first("paymentId", "payment_id", "razorpay_payment_id");
    return { orderId: st[oidK] || qs.get(oidK) || "", paymentId: st[pidK] || qs.get(pidK) || "" };
  }, [location.search, location.state]);

  // Page title + confetti
  useEffect(() => {
    document.title = "Payment Successful • Parivar Wedding Event";
  }, []);
  useEffect(() => {
    const host = confettiRef.current;
    if (!host) return;
    const colors = ["#22c55e", "#10b981", "#a3e635", "#fde047", "#60a5fa", "#f472b6"];
    const nodes = [];
    for (let i = 0; i < 70; i++) {
      const el = document.createElement("div");
      el.className = "sx-confetti-piece";
      el.style.left = Math.random() * 100 + "%";
      el.style.background = colors[i % colors.length];
      el.style.animationDelay = (Math.random() * 0.3).toFixed(2) + "s";
      host.appendChild(el);
      nodes.push(el);
    }
    const timer = setTimeout(() => nodes.forEach((n) => n.remove()), 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let abort = false;
    async function run() {
      setLoading(true);
      setError("");
      try {
        if (!orderId && !paymentId) {
          throw new Error("Missing order reference. We couldn't find an orderId or paymentId.");
        }

        // 1) Try REST API if configured
        const API = import.meta.env.VITE_ORDER_API_URL; // e.g. https://your.api
        if (API && orderId) {
          const r = await fetch(`${API.replace(/\/$/, "")}/orders/${encodeURIComponent(orderId)}`, {
            credentials: "include",
          });
          if (r.ok) {
            const json = await r.json();
            if (!abort) {
              setOrder(normalize(json));
              setLoading(false);
              return;
            }
          }
          // non-OK just falls through to Firestore
        }

        // 2) Firestore: orders/{orderId}
        if (db && orderId) {
          const ref = doc(db, "orders", orderId);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            if (!abort) {
              setOrder(normalize({ id: orderId, ...snap.data() }));
              setLoading(false);
              return;
            }
          }
        }

        // 3) Firestore: users/{uid}/orders/{orderId}
        if (db && auth?.currentUser && orderId) {
          const ref = doc(db, "users", auth.currentUser.uid, "orders", orderId);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            if (!abort) {
              setOrder(normalize({ id: orderId, ...snap.data() }));
              setLoading(false);
              return;
            }
          }
        }

        // 4) Firestore: collectionGroup('orders') by paymentId
        if (db && paymentId) {
          const q = query(collectionGroup(db, "orders"), where("paymentId", "==", paymentId));
          const qs = await getDocs(q);
          if (!qs.empty) {
            const docSnap = qs.docs[0];
            if (!abort) {
              setOrder(normalize({ id: docSnap.id, ...docSnap.data() }));
              setLoading(false);
              return;
            }
          }
        }

        throw new Error("We couldn't locate this order in the database.");
      } catch (e) {
        if (!abort) {
          setError(e.message || "Something went wrong while fetching your order.");
          setLoading(false);
        }
      }
    }
    run();
    return () => { abort = true; };
  }, [orderId, paymentId]);

  const downloadReceipt = () => {
    if (!order) return;
    const lines = [
      "==== Payment Receipt ====",
      `Status      : ${order.status || "SUCCESS"}`,
      `Date        : ${order.timestamp ? new Date(order.timestamp).toLocaleString() : "-"}`,
      `Customer    : ${order.customerName || "-"}`,
      `Email       : ${order.email || "-"}`,
      `Phone       : ${order.phone || "-"}`,
      `Order ID    : ${order.orderId || order.id || "-"}`,
      `Payment ID  : ${order.paymentId || "-"}`,
      `Amount      : ${fmtMoney(order.amount, order.currency)}`,
      `Method      : ${order.method || "-"}`,
      order.signature ? `Signature   : ${order.signature}` : null,
      "",
      "Thank you for your purchase with Parivar Wedding Event!",
    ]
      .filter(Boolean)
      .join("\n");

    const blob = new Blob([lines], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Parivar_Receipt_${order.orderId || order.id || "success"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={sx.page}>
      <style>{css}</style>

      <div style={sx.card}>
        <div ref={confettiRef} className="sx-confetti-host" />

        <div style={sx.iconWrap}>
          <svg viewBox="0 0 52 52" width="84" height="84" className="sx-check" aria-hidden="true">
            <circle className="sx-check-circle" cx="26" cy="26" r="25" fill="none" />
            <path className="sx-check-tick" fill="none" d="M14 27 l8 8 16-16" />
          </svg>
        </div>

        <h1 style={sx.title}>Payment Successful</h1>

        {loading && (
          <>
            <p style={sx.sub}>Looking up your order…</p>
            <div style={{ ...sx.skel, height: 14, width: 260, margin: "0 auto 16px" }} />
            <div style={sx.summaryGrid}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} style={sx.row}>
                  <div style={{ ...sx.skel, height: 12, width: 90 }} />
                  <div style={{ ...sx.skel, height: 12, width: "65%" }} />
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && error && (
          <>
            <p style={{ ...sx.sub, color: "#b91c1c" }}>{error}</p>
            <div style={sx.notice}>
              <div>
                <b>Tip:</b> Ensure you saved the order in your DB after payment capture.
                Pass <code>orderId</code> in router state or as a URL query (e.g. <code>?orderId=ORD-123</code>).
              </div>
              {(orderId || paymentId) && (
                <div style={{ marginTop: 6, color: "#4b5563" }}>
                  Searched for: {orderId ? <code>orderId={orderId}</code> : null}{" "}
                  {paymentId ? <code>paymentId={paymentId}</code> : null}
                </div>
              )}
            </div>
            <div style={sx.actions}>
              <button style={{ ...sx.btn, ...sx.btnGhost }} onClick={() => navigate("/")}>
                Continue Shopping
              </button>
              <button style={{ ...sx.btn, ...sx.btnOutline }} onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          </>
        )}

        {!loading && !error && order && (
          <>
            <p style={sx.sub}>
              Thank you! Your transaction has been completed{order.email ? (
                <> and a confirmation was sent to <b>{order.email}</b>.</>
              ) : null}
            </p>

            <div style={sx.summaryGrid}>
              <Row label="Customer" value={order.customerName} />
              <Row label="Phone" value={order.phone} />
              <Row label="Email" value={order.email} />
              <Row label="Amount" value={fmtMoney(order.amount, order.currency)} />
              <Row label="Order ID" value={order.orderId || order.id} />
              <Row label="Payment ID" value={order.paymentId} />
              <Row label="Method" value={order.method} />
              <Row label="Date" value={order.timestamp ? new Date(order.timestamp).toLocaleString() : "-"} />
              {order.signature ? <Row label="Signature" value={order.signature} /> : null}
              {order.status ? <Row label="Status" value={order.status} /> : null}
            </div>

            {Array.isArray(order.items) && order.items.length > 0 && (
              <div style={sx.items}>
                <div style={sx.itemsHead}>Items</div>
                {order.items.map((it, idx) => (
                  <div key={idx} style={sx.itemRow}>
                    <div style={{ fontWeight: 700 }}>{it.title || it.name || `Item ${idx + 1}`}</div>
                    <div style={{ color: "#6b7280" }}>
                      {it.qty ? `x${it.qty} • ` : ""}
                      {fmtMoney(it.amount, order.currency)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={sx.actions}>
              <button style={{ ...sx.btn, ...sx.btnPrimary }} onClick={() => navigate("/")}>
                Continue Shopping
              </button>
              <button style={{ ...sx.btn, ...sx.btnGhost }} onClick={() => navigate("/orders")}>
                View Orders
              </button>
              <button style={{ ...sx.btn, ...sx.btnOutline }} onClick={downloadReceipt}>
                Download Receipt
              </button>
            </div>

            <div style={sx.help}>
              Need help?{" "}
              <a href="mailto:support@parivar.events" style={sx.helpLink}>
                Contact Support
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={sx.row}>
      <div style={sx.rowLabel}>{label}</div>
      <div style={sx.rowValue}>{value || "—"}</div>
    </div>
  );
}

/* ---------- helpers ---------- */
function fmtMoney(amount, currency = "INR") {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  const major = Number.isFinite(n) && Number.isInteger(n) && n >= 1000 ? n / 100 : n;
  return new Intl.NumberFormat(currency === "INR" ? "en-IN" : undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(major);
}
function normalize(src = {}) {
  // Map common gateway/backend fields into a consistent object
  return {
    id: src.id || src.orderId || src.order_id,
    orderId: src.orderId || src.order_id || src.id,
    paymentId: src.paymentId || src.payment_id || src.razorpay_payment_id,
    amount: src.amount,
    currency: src.currency || "INR",
    method: src.method || src.paymentMethod,
    email: src.email || src.customerEmail,
    customerName: src.customerName || src.name,
    phone: src.phone || src.contact,
    timestamp: src.timestamp || src.paidAt || src.createdAt,
    signature: src.signature || src.razorpay_signature,
    status: src.status || "SUCCESS",
    items: src.items,
  };
}

/* ---------- styles ---------- */
const sx = {
  page: {
    minHeight: "100dvh",
    display: "grid",
    placeItems: "center",
    background: "radial-gradient(1200px 600px at 50% -10%, #fffaf5 10%, #fff 60%)",
    padding: "40px 16px",
    fontFamily:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji','Segoe UI Emoji'",
    color: "#18181b",
  },
  card: {
    width: "100%",
    maxWidth: 820,
    background: "#ffffff",
    border: "1px solid #f1eff6",
    borderRadius: 20,
    boxShadow: "0 12px 50px rgba(20,20,40,0.06)",
    padding: "28px 26px 30px",
    position: "relative",
    overflow: "hidden",
  },
  iconWrap: { display: "grid", placeItems: "center", marginTop: 8, marginBottom: 4 },
  title: { fontSize: "1.9rem", fontWeight: 800, letterSpacing: ".2px", textAlign: "center", margin: "8px 0 6px" },
  sub: { textAlign: "center", color: "#4b5563", marginBottom: 12 },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    background: "#faf9fe",
    border: "1px solid #efecf8",
    borderRadius: 16,
    padding: 16,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    background: "#fff",
    border: "1px solid #f0eef6",
    borderRadius: 12,
    minHeight: 48,
  },
  rowLabel: { flex: "0 0 118px", color: "#6b7280", fontWeight: 700, fontSize: 13, letterSpacing: ".02em" },
  rowValue: { fontWeight: 800, fontSize: 15, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  actions: { display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginTop: 16 },
  btn: {
    borderRadius: 999,
    padding: "10px 16px",
    fontWeight: 900,
    letterSpacing: ".02em",
    border: "1.5px solid transparent",
    cursor: "pointer",
    transition: "transform .12s ease, box-shadow .12s ease, background .2s ease",
  },
  btnPrimary: { background: "linear-gradient(135deg, #22c55e, #10b981)", color: "#fff", boxShadow: "0 6px 18px rgba(16,185,129,.25)" },
  btnGhost: { background: "#111827", color: "#fff" },
  btnOutline: { background: "#fff", color: "#1f2937", borderColor: "#e5e7eb" },
  help: { textAlign: "center", marginTop: 14, color: "#6b7280", fontSize: 14 },
  helpLink: { color: "#2563eb", fontWeight: 800, textDecoration: "none" },
  notice: {
    background: "#fff7ed",
    border: "1px solid #ffedd5",
    color: "#7c2d12",
    padding: "10px 12px",
    borderRadius: 12,
    margin: "8px 0 4px",
    fontSize: 14,
  },
  skel: {
    background:
      "linear-gradient(90deg, #f4f4f7 25%, #eeeeff 37%, #f4f4f7 63%)",
    backgroundSize: "400% 100%",
    animation: "sx-shimmer 1.2s ease-in-out infinite",
    borderRadius: 8,
  },
  items: {
    marginTop: 14,
    background: "#fff",
    border: "1px solid #efecf8",
    borderRadius: 12,
    overflow: "hidden",
  },
  itemsHead: {
    padding: "10px 12px",
    background: "#faf9fe",
    borderBottom: "1px solid #efecf8",
    fontWeight: 800,
  },
  itemRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 12px",
    borderTop: "1px dashed #f1eff6",
  },
};

const css = `
.sx-check { overflow: visible; }
.sx-check-circle {
  stroke: #10b981; stroke-width: 3;
  stroke-dasharray: 166; stroke-dashoffset: 166;
  animation: sx-stroke 0.8s cubic-bezier(.65,.05,.36,1) forwards;
  filter: drop-shadow(0 0 8px rgba(16,185,129,.35));
}
.sx-check-tick {
  stroke: #10b981; stroke-width: 4; stroke-linecap: round; stroke-linejoin: round;
  stroke-dasharray: 48; stroke-dashoffset: 48;
  animation: sx-stroke 0.6s 0.35s cubic-bezier(.65,.05,.36,1) forwards;
}
@keyframes sx-stroke { to { stroke-dashoffset: 0; } }
.sx-confetti-host { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.sx-confetti-piece {
  position: absolute; top: -10px; width: 8px; height: 14px; border-radius: 2px;
  animation: sx-fall 2.2s ease-in forwards, sx-spin 1.2s linear infinite;
}
@keyframes sx-fall { 0% { transform: translateY(-10px) rotate(0deg); } 100% { transform: translateY(120vh) rotate(240deg); } }
@keyframes sx-spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
@keyframes sx-shimmer { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }
@media (max-width: 680px) {
  div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
}
`;

export default Success;
